import { Injectable } from '@angular/core';
import { EstimateTab } from '../../models/dtos';
import { CommonDataService } from '../common-data.service';

/** EstimateTab flags recording whether the sell price drove a Line Item row's last edit. */
export type MaterialSellPriceFlag = Extract<keyof EstimateTab, `isLastModified${string}`>;

export type MaterialCategoryKey = 'siemensMaterial' | 'outsideMaterial' | 'mechanicalMaterial' | 'electricalMaterial';
export type MaterialSubRowKey = 'headerRow' | 'shippingAndHandling' | 'warranty';

export const MATERIAL_SELL_PRICE_FLAGS: Record<MaterialCategoryKey, Record<MaterialSubRowKey, MaterialSellPriceFlag>> = {
    siemensMaterial: {
        headerRow: 'isLastModifiedSiemensMaterialSellPrice',
        shippingAndHandling: 'isLastModifiedSiemensMaterialSHSellPrice',
        warranty: 'isLastModifiedSiemensMaterialWarrantySellPrice'
    },
    outsideMaterial: {
        headerRow: 'isLastModifiedOutsideMaterialSellPrice',
        shippingAndHandling: 'isLastModifiedOutsideMaterialSHSellPrice',
        warranty: 'isLastModifiedOutsideMaterialWarrantySellPrice'
    },
    mechanicalMaterial: {
        headerRow: 'isLastModifiedMechMaterialSellPrice',
        shippingAndHandling: 'isLastModifiedMechMaterialSHSellPrice',
        warranty: 'isLastModifiedMechMaterialWarrantySellPrice'
    },
    electricalMaterial: {
        headerRow: 'isLastModifiedElecMaterialSellPrice',
        shippingAndHandling: 'isLastModifiedElecMaterialSHSellPrice',
        warranty: 'isLastModifiedElecMaterialWarrantySellPrice'
    }
};

/** Section label (translation key) used by the material row component → model category key. */
export const MATERIAL_CATEGORY_BY_LABEL: Record<string, MaterialCategoryKey> = {
    SIEMENS_MATERIAL: 'siemensMaterial',
    OUTSIDE_MATERIAL: 'outsideMaterial',
    MECHANICAL_MATERIAL: 'mechanicalMaterial',
    ELECTRICAL_MATERIAL: 'electricalMaterial'
};


/** EstimateTab flags recording whether the sell price drove a Line Item row's last edit for labor. */
export type LaborSellPriceFlag = Extract<keyof EstimateTab, `isLastModified${string}`>;

export type LaborCategoryKey = 'projectManagement' | 'engineering' | 'cyberSecurityEngineering' | 'energyEngineering' | 'electricalEngineering' | 'networkEngineering' | 'qualificationEngineering' | 'electrical' | 'mechanical' | 'specialist' | 'programming' | 'graphics' | 'digitalDeploymentSpecialist';

/** Section label (translation key) used by the labor row component → model category key. */
export const LABOR_CATEGORY_BY_LABEL: Record<string, LaborCategoryKey> = {
    PROJECT_MANAGEMENT: 'projectManagement',
    ENGINEERING: 'engineering',
    CYBER_SECURITY_ENGINEERING: 'cyberSecurityEngineering',
    ENERGY_ENGINEERING: 'energyEngineering',
    ELECTRICAL_ENGINEERING: 'electricalEngineering',
    NETWORK_ENGINEERING: 'networkEngineering',
    QUALIFICATION_ENGINEERING: 'qualificationEngineering',
    ELECTRICAL: 'electrical',
    MECHANICAL: 'mechanical',
    SPECIALIST: 'specialist',
    PROGRAMMING: 'programming',
    GRAPHICS: 'graphics',
    DIGITAL_DEPLOYMENT_SPECIALIST: 'digitalDeploymentSpecialist'
};

/** EstimateTab flags recording whether the sell price drove a Line Item row's last edit for labor by category. */
export const LABOR_SELL_PRICE_FLAGS: Record<LaborCategoryKey, LaborSellPriceFlag> = {
    projectManagement: 'isLastModifiedPMSellPrice',
    engineering: 'isLastModifiedENGSellPrice',
    cyberSecurityEngineering: 'isLastModifiedCENGSellPrice',
    energyEngineering: 'isLastModifiedEENGSellPrice',
    electricalEngineering: 'isLastModifiedLENGSellPrice',
    networkEngineering: 'isLastModifiedNENGSellPrice',
    qualificationEngineering: 'isLastModifiedQENGSellPrice',
    electrical: 'isLastModifiedELECSellPrice',
    mechanical: 'isLastModifiedMECHellPrice',
    specialist: 'isLastModifiedSPECSellPrice',
    programming: 'isLastModifiedProgSellPrice',
    graphics: 'isLastModifiedGrapSellPrice',
    digitalDeploymentSpecialist: 'isLastModifiedDdepSellPrice'
};

/** EstimateTab flags recording whether the sell price drove a Line Item row's last edit for subcontractors. */
export type SubcontractorSellPriceFlag = Extract<keyof EstimateTab, `isLastModifiedSubContractor${string}`>;

/**
 * Generates the sell-price flag name for a subcontractor row by index.
 * E.g. index 0 → 'isLastModifiedSubContractor0SellPrice'
 */
export function getSubcontractorSellPriceFlag(index: number): SubcontractorSellPriceFlag {
    return `isLastModifiedSubContractor${index}SellPrice` as SubcontractorSellPriceFlag;
}

/** EstimateTab flags for misc/travel/risk rows identified by row name. */
export type MiscRiskTravelSellPriceFlag = Extract<keyof EstimateTab, `isLastModifiedMisc${string}SellPrice` | `isLastModifiedTravel${string}SellPrice` | `isLastModifiedRisk${string}SellPrice` | `isLastModifiedAutoRisk${string}SellPrice` | `isLastModifiedMechFee${string}SellPrice` | `isLastModifiedSubcontractorFee${string}SellPrice` | `isLastModifiedProductivityReserve${string}SellPrice`>;

/** Map of row name (e.g., 'Miscellaneous', 'Travel') → flag name for sell-price highlighting. */
export const MISC_RISK_TRAVEL_SELL_PRICE_FLAGS: Record<string, MiscRiskTravelSellPriceFlag> = {
    'MISCELLANEOUS_COSTS': 'isLastModifiedMiscSellPrice',
    'TRAVEL_EXPENSE_COSTS': 'isLastModifiedTravelSellPrice',
    'MECHANICAL_FEE': 'isLastModifiedMechFeeSellPrice',
    'SUBCONTRACTOR_FEE': 'isLastModifiedSubcontractorFeeSellPrice',
    'PRODUCTIVITY_RESERVE': 'isLastModifiedProductivityReserveSellPrice'
};

/** Map of risk row name → flag name for sell-price highlighting. */
export const RISK_SELL_PRICE_FLAGS: Record<string, MiscRiskTravelSellPriceFlag> = {
    'CALCULATED_RISK_COSTS': 'isLastModifiedAutoRiskSellPrice',
    'PROJECT_ENTERED_RISK_COSTS': 'isLastModifiedRiskSellPrice'
};

@Injectable({
    providedIn: 'root'
})
export class EstimateLastModifiedSellPriceService {


    constructor(private readonly commonDataService: CommonDataService) { }

    /** Records whether the sell price or the GM%/MU% field drove the material row's last edit. */
    setMaterialSellPriceFlag(
        category: MaterialCategoryKey,
        subRow: MaterialSubRowKey,
        isSellPriceEdit: boolean
    ): void {
        const estimateMetaData = this.commonDataService.getEstimateMetaData();
        const estimateTab = estimateMetaData?.estimateTab;
        if (estimateTab) {
            (estimateTab as EstimateTab)[MATERIAL_SELL_PRICE_FLAGS[category][subRow]] = isSellPriceEdit;
        }
    }

    /** Records whether the sell price or the GM%/MU% field drove the labor row's last edit. */
    setLaborSellPriceFlag(
        category: LaborCategoryKey,
        isSellPriceEdit: boolean
    ): void {
        const estimateMetaData = this.commonDataService.getEstimateMetaData();
        const estimateTab = estimateMetaData?.estimateTab;
        if (estimateTab) {
            (estimateTab as EstimateTab)[LABOR_SELL_PRICE_FLAGS[category]] = isSellPriceEdit;
        }
    }

    /** Records whether the sell price or the GM%/MU% field drove a misc/travel row's last edit. */
    setMiscTravelSellPriceFlag(
        rowName: string,
        isSellPriceEdit: boolean
    ): void {
        const estimateMetaData = this.commonDataService.getEstimateMetaData();
        const estimateTab = estimateMetaData?.estimateTab;
        if (estimateTab) {
            const flagName = MISC_RISK_TRAVEL_SELL_PRICE_FLAGS[rowName];
            if (flagName) {
                (estimateTab as EstimateTab)[flagName] = isSellPriceEdit;
            }
        }
    }

    /** Records whether the sell price or the GM%/MU% field drove a risk row's last edit. */
    setRiskSellPriceFlag(
        rowName: string,
        isSellPriceEdit: boolean
    ): void {
        const estimateMetaData = this.commonDataService.getEstimateMetaData();
        const estimateTab = estimateMetaData?.estimateTab;
        if (estimateTab) {
            const flagName = RISK_SELL_PRICE_FLAGS[rowName];
            if (flagName) {
                (estimateTab as EstimateTab)[flagName] = isSellPriceEdit;
            }
        }
    }

    // ── Read flags ─────────────────────────────────────────────────────

    /** True when the sell price — not the margin — drove the material row's last edit. */
    isMaterialSellPriceLastModified(category: MaterialCategoryKey, subRow: MaterialSubRowKey): boolean {
        return this.readFlag(MATERIAL_SELL_PRICE_FLAGS[category][subRow]);
    }

    /** True when the sell price — not the margin — drove the labor row's last edit. */
    isLaborSellPriceLastModified(category: LaborCategoryKey): boolean {
        return this.readFlag(LABOR_SELL_PRICE_FLAGS[category]);
    }


    /** True when the sell price — not the margin — drove a misc/travel/risk row's last edit. */
    isNamedRowSellPriceLastModified(rowName: string): boolean {
        const flagName = MISC_RISK_TRAVEL_SELL_PRICE_FLAGS[rowName] ?? RISK_SELL_PRICE_FLAGS[rowName];
        return flagName ? this.readFlag(flagName) : false;
    }

    /** Flags are absent until the user edits a sell price, which makes the margin the default. */
    private readFlag(flagName: keyof EstimateTab): boolean {
        const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
        return (estimateTab as unknown as Record<string, unknown>)?.[flagName as string] === true;
    }

    // ── Reset all sell price flags ─────────────────────────────────────
    resetAllSellPriceFlags(): void {
        const estimateMetaData = this.commonDataService.getEstimateMetaData();
        const estimateTab = estimateMetaData?.estimateTab;
        if (!estimateTab) {
            return;
        }

        // Reset all material sell price flags (nested: category → subRow → flag)
        for (const category of Object.values(MATERIAL_SELL_PRICE_FLAGS)) {
            for (const flagName of Object.values(category)) {
                (estimateTab as any)[flagName] = false;
            }
        }

        // Reset all labor sell price flags (flat: category → flag)
        for (const flagName of Object.values(LABOR_SELL_PRICE_FLAGS)) {
            (estimateTab as any)[flagName] = false;
        }


        // Reset all misc/travel sell price flags
        for (const flagName of Object.values(MISC_RISK_TRAVEL_SELL_PRICE_FLAGS)) {
            (estimateTab as any)[flagName] = false;
        }

        // Reset all risk sell price flags
        for (const flagName of Object.values(RISK_SELL_PRICE_FLAGS)) {
            (estimateTab as any)[flagName] = false;
        }
    }

}